<template lang="pug">
.window(
	ref="win"
	v-show="!minimized"
	:style="style"
	@mousedown.stop="requestActive"
	@mouseenter.stop.prevent="mouseEnter"
	@mouseleave.stop.prevent="mouseLeave"
	)
	slot(name="header")
		header(@mousedown="startDragMove" @dblclick="maximize")
			slot(name="title")
				.title {{ title || "Window" }}
			.actions
				button.minimize(v-if="isMinimizable" @click="minimize"): i.fa.fa-window-minimize
				button.maximize(v-if="isMaximizable" @click="maximize"): i.fa.fa-window-maximize
				button.close(v-if="isClosable" @click="close"): i.fa.fa-window-close

	slot(ref="content")
		.content
			component(:is="component" v-bind="wrappedComponentProps" ref="component" v-on="componentEventHandlers")

	slot(name="footer")
		footer
			.content
			.resize-handle-container
				.resize-handle(v-if="isResizable" @mousedown="startDragResize"): i.fa.fa-grip-lines
</template>

<script>
import Vue from "vue";
import _ from "lodash";
import { Component } from "vue-property-decorator";
import { mixins } from "vue-class-component";
import ViewportAware from "@/mixins/ViewportAware";

@Component({
	props: {
		title: {
			required: true
		},
		component: {
			default: null
		},
		componentEventHandlers: {
			default: () => {}
		},
		componentProps: {
			default: () => {}
		},
		x: { default: null },
		y: { default: null },
		width: { default: null },
		height: { default: null },
		minWidth: { default: 150 },
		minHeight: { default: "5em" },
		active: { default: false },
		minimized: { default: false },
		maximized: { default: false },
		movable: { default: true },
		resizable: { default: false },
		collideBoundaries: { default: true },
		closable: { default: true },
		maximizable: { default: true },
		minimizable: { default: true },
		selfManaged: { default: true },
		opacity: { default: 1.0 },
		activeOpacity: { default: 1.0 },
		background: { default: null },
		zIndex: {
			default: 0
		},
		noMountedMove: {
			default: false
		},
		activeZIndex: {
			default: 100
		}
	}
})
class Window extends mixins(ViewportAware) {
	dragMoveStart = { x: 0, y: 0 };
	sx = 200;
	ox = 200;
	sy = 200;
	oy = 200;
	sw = 300;
	sh = 200;

	constructor() {
		super();
	}

	mounted() {
		this.stopDragMove.bind(this);
		this.dragMove.bind(this);
		this.stopDragResize.bind(this);
		this.dragResize.bind(this);
		if (this.x !== null) this.sx = this.x;
		if (this.y !== null) this.sy = this.y;
		if (this.width !== null) this.sw = this.width;
		if (this.height !== null) this.sh = this.height;
		this.ox = this.x;
		this.oy = this.y;
		if (!this.noMountedMove) this.dragMove({ x: this.x, y: this.y });
	}

	get wrappedComponentProps() {
		// return this.componentProps;
		return _.mapValues(this.componentProps, p => {
			if (typeof p === "function") return p();
			else return p;
		});
	}

	get isMinimizable() {
		return !this.selfManaged && this.minimizable;
	}

	get isMaximizable() {
		return !this.selfManaged && this.maximizable;
	}

	get isClosable() {
		return !this.selfManaged && this.closable;
	}

	get position() {
		if (this.maximized) return { x: 0, y: 0 };
		if (this.isSelfPositioned) return { x: this.sx, y: this.sy };
		else return { x: this.x, y: this.y };
	}

	get size() {
		if (this.maximized) return this.viewportData.viewportSize;
		if (this.isSelfPositioned) return { w: this.sw, h: this.sh };
		else return { w: this.width, h: this.height };
	}

	get isSelfPositioned() {
		return this.selfManaged || (this.x === null && this.y === null);
	}

	get isSelfSized() {
		return this.selfManaged || (this.width === null && this.height === null);
	}

	get style() {
		let { x, y } = this.position;
		let { w, h } = this.size;
		const ret = {
			position: "fixed",
			top: `${y}px`,
			left: `${x}px`,
			"min-width": this.numAsPxOrString(this.minWidth),
			"min-height": this.numAsPxOrString(this.minHeight),
			width: this.numAsPxOrString(w),
			height: this.numAsPxOrString(h),
			opacity: this.active ? this.activeOpacity : this.opacity,
			zIndex: this.active ? this.activeZIndex : this.zIndex
		};
		if (this.background) ret.background = this.background;
		return ret;
	}

	get boundaries() {
		const winel = this.$refs["win"];
		const rect = winel.getBoundingClientRect();
		return {
			minx: 0,
			maxx: this.viewportData.viewportSize.w - rect.width,
			miny: 0,
			maxy: this.viewportData.viewportSize.h - rect.height,
			minw: 0,
			maxw: this.viewportData.viewportSize.w - this.position.x,
			minh: 0,
			maxh: this.viewportData.viewportSize.h - this.position.y
		};
	}

	get isMovable() {
		return !this.maximized && this.movable;
	}

	get isResizable() {
		return (
			!this.maximized &&
			this.resizable &&
			typeof this.width == "number" &&
			typeof this.height == "number"
		);
	}

	numAsPxOrString(val) {
		return typeof val == "number" ? `${val}px` : val;
	}

	startDragMove(ev) {
		// console.debug("Window.startDragMove", ev);
		if (!this.isMovable) return;
		if (ev.button != 0) return;
		this.$emit("move:begin");
		this.dragMoveStart = { x: ev.x - ev.layerX, y: ev.y - ev.layerY };
		this.ox = this.x - ev.layerX;
		this.oy = this.y - ev.layerY;
		window.addEventListener("mouseup", this.stopDragMove);
		document.addEventListener("mousemove", this.dragMove);
	}

	dragMove(e) {
		// console.debug("window.dragMove", e);
		this.sx = this.ox + (e.x - this.dragMoveStart.x);
		this.sy = this.oy + (e.y - this.dragMoveStart.y);
		if (this.collideBoundaries) {
			const { minx, maxx, miny, maxy } = this.boundaries;
			this.sx = Math.max(minx, Math.min(this.sx, maxx));
			this.sy = Math.max(miny, Math.min(this.sy, maxy));
		}
		this.$emit("move", { x: this.sx, y: this.sy });
	}

	stopDragMove() {
		this.$emit("move:end");
		window.removeEventListener("mouseup", this.stopDragMove);
		document.removeEventListener("mousemove", this.dragMove);
	}

	startDragResize(ev) {
		if (!this.isResizable) return;
		if (ev.button != 0) return;
		this.$emit("resize:begin");
		window.addEventListener("mouseup", this.stopDragResize);
		document.addEventListener("mousemove", this.dragResize);
	}

	dragResize(e) {
		// console.debug(e);
		this.sw += e.movementX;
		this.sh += e.movementY;
		if (this.collideBoundaries) {
			const { minw, maxw, minh, maxh } = this.boundaries;
			this.sw = Math.max(minw, Math.min(this.sw, maxw));
			this.sh = Math.max(minh, Math.min(this.sh, maxh));
		}
		this.$emit("resize", { w: this.sw, h: this.sh });
	}

	stopDragResize() {
		this.$emit("resize:end");
		this.afterResize();
		window.removeEventListener("mouseup", this.stopDragResize);
		document.removeEventListener("mousemove", this.dragResize);
	}

	afterResize() {
		const content = this.$refs["content"];
		const component = this.$refs["component"];

		if (component)
			if (typeof component.parentWindowResizeEnd == "function")
				component.parentWindowResizeEnd();

		if (content)
			if (typeof content.parentWindowResizeEnd == "function")
				content.parentWindowResizeEnd();
	}

	maximize() {
		this.$emit("maximize");
		Vue.nextTick(() => this.afterResize());
	}

	minimize() {
		this.$emit("minimize");
		Vue.nextTick(() => this.afterResize());
	}

	close() {
		this.$emit("close");
	}

	requestActive() {
		this.$emit("request:active");
	}

	mouseEnter(ev) {
		this.$emit("mouseenter", ev);
	}

	mouseLeave(ev) {
		this.$emit("mouseleave", ev);
	}
}

export default Window;
</script>

<style lang="stylus" scoped>
.window
	display -webkit-box
	flex-direction column
	border-radius 0.1em
	background rgba(255, 255, 255, 0.9)
	box-shadow 0 0.2em 0.8em rgba(0, 0, 0, 0.1), 0 0.1em 0.1em rgba(0, 0, 0, 0.2)
	transition opacity 0.2s ease

	header
		display flex
		justify-content space-between
		align-items center
		text-align left
		background #fff
		user-select none
		border-bottom solid 0.1rem rgba(0,0,0,0.3)
		cursor move

		.title
			font-weight bold
			white-space nowrap
			overflow hidden
			text-overflow ellipsis
			max-width calc(100% - 4.5em)
			padding 0.25em 0.5em

		button
			padding 0.25em 0.1em
			color #000
			opacity 0.5
			transition opacity 0.2s ease
			border none
			background none
			cursor pointer

			&:last-of-type
				padding-right 0.5em

			&:hover
				opacity 1

	> .content
		position relative
		overflow auto
		flex 1
		z-index 5
		// height fit-content

	&.nooverflow .content, .content.nooverflow
		overflow hidden

	&.center > .content
		display flex
		justify-content center
		align-items center

	footer
		display flex
		justify-content flex-end
		margin-top auto
		height 0

		.resize-handle-container
			z-index 2050
			position absolute
			overflow hidden
			width 0.55em
			height 0.60em
			right 0
			bottom 0

			.resize-handle
				text-shadow 0 0.1em 0.05em rgba(255, 255, 255, 0.8)
				color inherit
				font-size 80%
				position relative
				transform rotate(-45deg)
				opacity 0.5
				cursor nwse-resize

				&:hover
					opacity 1
</style>
